import type { OrganizationType, SubscriptionType } from '@form-factory/schema';
import {
  BillingInterval,
  Currency,
  SubscriptionTier,
} from '@form-factory/schema/enums';
import { formatUtils } from '@form-factory/shared';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, toast } from '@utima/ui';
import { ArrowRight, Check } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Card } from '@/components/card/Card';

import { ChangePlanDialog } from './components/ChangePlanDialog';
import { useSubscriptionManager } from './hooks/useSubscriptionManager';
import { trpc } from '../../services/trpc';

/**
 * Props for the SubscriptionManager component
 */
export interface SubscriptionManagerProps {
  organization: OrganizationType;
  activeSubscription: SubscriptionType;
  onUpdate: () => void;
}

export function SubscriptionManager({
  organization,
  activeSubscription,
  onUpdate,
}: SubscriptionManagerProps) {
  const { t } = useLingui();
  const orgCurrency = organization.currency ?? Currency.USD;
  const [displayCurrency, setDisplayCurrency] = useState<Currency>(orgCurrency);
  const [isChangePlanDialogOpen, setIsChangePlanDialogOpen] = useState(false);

  const [selectedPlan, setSelectedPlan] = useState<SubscriptionTier | null>(
    null,
  );
  const [selectedInterval, setSelectedInterval] = useState<BillingInterval>(
    BillingInterval.Month,
  );

  const {
    data: tierDetails,
    isLoading: isLoadingTiers,
    isError: isTierDetailsError,
    error: tierDetailsError,
  } = trpc.subscriptions.getTierDetails.useQuery(undefined, {
    staleTime: Infinity,
  });

  const { isLoading } = useSubscriptionManager(organization.id);

  // Update display currency if org currency changes
  useEffect(() => {
    setDisplayCurrency(orgCurrency);
  }, [orgCurrency]);

  useEffect(() => {
    if (activeSubscription) {
      setSelectedInterval(
        activeSubscription.billingInterval || BillingInterval.Month,
      );
    } else {
      setSelectedInterval(BillingInterval.Month);
    }
  }, [activeSubscription]);

  // Effect to handle tier details loading error
  useEffect(() => {
    if (isTierDetailsError && tierDetailsError) {
      console.error('Failed to load tier details:', tierDetailsError);
      toast.error(
        t`Failed to load subscription plan details. Please refresh the page.`,
      );
    }
  }, [isTierDetailsError, tierDetailsError, t]); // Add t to dependency array for lingui

  const handlePlanChange = (plan: SubscriptionTier) => {
    setSelectedPlan(plan);
    const tier = tierDetails?.find(t => t.id === plan);
    if (tier) {
      const hasMonthly = tier.prices.some(
        p => p.interval === BillingInterval.Month && p.currency === orgCurrency,
      );
      const hasYearly = tier.prices.some(
        p => p.interval === BillingInterval.Year && p.currency === orgCurrency,
      );
      if (hasMonthly && !hasYearly) {
        setSelectedInterval(BillingInterval.Month);
      } else if (!hasMonthly && hasYearly) {
        setSelectedInterval(BillingInterval.Year);
      }
    }
    setIsChangePlanDialogOpen(true);
  };

  if (isLoadingTiers || isLoading) {
    return (
      <div className='p-4 text-center'>
        <Trans>Loading subscription details...</Trans>
      </div>
    );
  }

  // Check if user is on free plan
  const isFreePlan =
    !activeSubscription || activeSubscription.tier === SubscriptionTier.Free;

  // Find the Form Factory plan (using Standard tier as the base)
  const formFactoryPlan = tierDetails?.find(
    tier => tier.id === SubscriptionTier.Standard,
  );

  return (
    <div className='space-y-6 pb-8'>
      <div className='space-y-4'>
        <h3 className='text-xl font-semibold'>
          <Trans>Available Plans</Trans>
        </h3>
        {isLoadingTiers ? (
          <div className='p-4 text-center'>
            <Trans>Loading plans...</Trans>
          </div>
        ) : (
          <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
            {/* Free Plan Card */}
            {(isFreePlan || !activeSubscription) && (
              <Card
                key='free-plan'
                title={t`Free`}
                collapsible={false}
                className={`flex h-full flex-col ${isFreePlan ? 'border-primary ring-2 ring-primary' : ''}`}
              >
                <div className='min-h-[230px] grow'>
                  <div className='mb-4 flex items-baseline gap-1'>
                    <span className='text-3xl font-bold'>{t`Free`}</span>
                  </div>

                  <div className='mb-6 space-y-2'>
                    {tierDetails
                      ?.find(t => t.id === SubscriptionTier.Free)
                      ?.features?.map((feature, index) => (
                        <p key={index} className='flex items-center text-sm'>
                          <Check className='mr-2 size-4 shrink-0 text-primary' />
                          <span>{feature}</span>
                        </p>
                      ))}
                    <p className='flex items-center text-sm'>
                      <Check className='mr-2 size-4 shrink-0 text-primary' />
                      <span>
                        <Trans>Up to 10 documents</Trans>
                      </span>
                    </p>
                    <p className='flex items-center text-sm'>
                      <Check className='mr-2 size-4 shrink-0 text-primary' />
                      <span>
                        <Trans>Up to 5 users</Trans>
                      </span>
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* Form Factory Plan Card */}
            {formFactoryPlan && (
              <Card
                key='form-factory-plan'
                title={t`Form Factory`}
                collapsible={false}
                className={`flex h-full flex-col ${isFreePlan ? '' : 'border-primary ring-2 ring-primary'}`}
              >
                <div className='min-h-[230px] grow'>
                  <div className='mb-4 flex items-baseline gap-1'>
                    <span className='text-3xl font-bold'>
                      {formFactoryPlan &&
                      formFactoryPlan.prices.some(
                        p =>
                          p.interval === BillingInterval.Month &&
                          p.currency === displayCurrency,
                      )
                        ? formatUtils.currency(
                            formFactoryPlan.prices.find(
                              p =>
                                p.interval === BillingInterval.Month &&
                                p.currency === displayCurrency,
                            )!.amount,
                            displayCurrency,
                          )
                        : t`N/A`}
                    </span>
                    <span className='text-sm text-gray-500'>/ {t`mo`}</span>
                  </div>

                  {formFactoryPlan &&
                    formFactoryPlan.prices.find(
                      p =>
                        p.interval === BillingInterval.Year &&
                        p.currency === displayCurrency,
                    ) && (
                      <div className='-mt-3 mb-4 text-sm text-gray-500'>
                        {t`or `}
                        {formatUtils.currency(
                          formFactoryPlan.prices.find(
                            p =>
                              p.interval === BillingInterval.Year &&
                              p.currency === displayCurrency,
                          )!.amount,
                          displayCurrency,
                        )}{' '}
                        / {t`yr`}
                      </div>
                    )}

                  <div className='mb-6 space-y-2'>
                    <p className='flex items-center text-sm'>
                      <Check className='mr-2 size-4 shrink-0 text-primary' />
                      <span>
                        <Trans>Monthly server and storage fee included</Trans>
                      </span>
                    </p>
                    <p className='flex items-center text-sm'>
                      <Check className='mr-2 size-4 shrink-0 text-primary' />
                      <span>
                        <Trans>No documents limit</Trans>
                      </span>
                    </p>
                    <p className='flex items-center text-sm'>
                      <Check className='mr-2 size-4 shrink-0 text-primary' />
                      <span>
                        <Trans>No admin users limit</Trans>
                      </span>
                    </p>
                  </div>
                </div>
                <div className='mt-auto'>
                  {isFreePlan ? (
                    <Button
                      className='mt-auto w-full'
                      variant='primary'
                      onClick={() =>
                        handlePlanChange(SubscriptionTier.Standard)
                      }
                      disabled={isLoadingTiers}
                      icon={<ArrowRight className='ml-2 size-4' />}
                    >
                      <Trans>Change Plan</Trans>
                    </Button>
                  ) : (
                    <p className='text-center text-lg text-primary'>
                      <Trans>You are currently on this plan.</Trans>
                    </p>
                  )}
                </div>
              </Card>
            )}
          </div>
        )}
      </div>

      {selectedPlan && (
        <ChangePlanDialog
          isOpen={isChangePlanDialogOpen}
          onClose={() => setIsChangePlanDialogOpen(false)}
          currentTier={activeSubscription?.tier || SubscriptionTier.Free}
          selectedTier={selectedPlan}
          organizationId={organization.id}
          onSubscriptionUpdate={() => {
            setIsChangePlanDialogOpen(false);
            if (onUpdate) onUpdate();
          }}
          selectedInterval={selectedInterval}
        />
      )}
    </div>
  );
}
